import type { ReactNode } from "react";
import { Button } from "./Button";

interface ScreenStateProps {
  loading: boolean;
  error: Error | null;
  onRetry?: (() => void) | undefined;
  /** Replace the generic error card, e.g. for a locked step that should explain what to do first. */
  renderError?: ((error: Error) => ReactNode) | undefined;
  children: ReactNode;
}

/**
 * Loading and error states, once.
 *
 * A skeleton, never a spinner on a blank screen: on a slow connection a spinner tells a participant nothing about
 * whether to wait. The error names what failed and offers a retry rather than leaving the screen empty.
 */
export function ScreenState({ loading, error, onRetry, renderError, children }: ScreenStateProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 pt-6" role="status" aria-label="Loading">
        {[0, 1, 2].map((row) => (
          <div
            key={row}
            className="h-20 animate-pulse rounded-[var(--radius-card)] border border-border bg-surface-high"
          />
        ))}
      </div>
    );
  }

  if (error) {
    if (renderError) return <>{renderError(error)}</>;
    return (
      <div className="flex flex-col items-start gap-3 py-10">
        <h2 className="text-[22px] text-accent">We couldn't load that.</h2>
        <p className="m-0 max-w-[480px] text-[14.5px] text-text-muted">{error.message}</p>
        {onRetry ? <Button onClick={onRetry}>Try again</Button> : null}
      </div>
    );
  }

  return <>{children}</>;
}
